import { useAlertNotifyStore } from "../stores/use-alert-notify-store";
import { getAlarmSound, ALARM_PATTERN_MS } from "./alarm-sounds";

let audioContext: AudioContext | null = null;

function getAudioContext(): AudioContext | null {
  if (typeof window === "undefined") return null;
  try {
    const AudioContextConstructor =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextConstructor) return null;
    audioContext = audioContext ?? new AudioContextConstructor();
    return audioContext;
  } catch {
    return null;
  }
}

/** Call from a real user gesture so later attention batches may play sound. */
export function prepareAlertSound(): void {
  const context = getAudioContext();
  if (context?.state === "suspended") void context.resume().catch(() => {});
}

/**
 * The bus every in-flight pattern is routed through. A pattern schedules ~10s of
 * oscillators up front, so the only way to cut it short is to kill the node they
 * all feed — individual oscillators are already scheduled and cannot be recalled.
 */
let currentBus: GainNode | null = null;

/** Silence whatever is playing right now. Safe to call when nothing is. */
export function stopAlertSound(): void {
  if (!currentBus) return;
  try {
    // Ramp over a few ms rather than cutting instantly: an abrupt gain change
    // mid-waveform produces an audible click.
    const ctx = getAudioContext();
    if (ctx) {
      currentBus.gain.cancelScheduledValues(ctx.currentTime);
      currentBus.gain.setValueAtTime(currentBus.gain.value, ctx.currentTime);
      currentBus.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.02);
    }
    const bus = currentBus;
    setTimeout(() => bus.disconnect(), 60);
  } catch {
    /* context already torn down */
  }
  currentBus = null;
}

/** Whether a pattern is currently sounding. */
export const isAlertSoundPlaying = (): boolean => currentBus != null;

/**
 * Play ONE ~10s alarm pattern — the operator's chosen one, unless overridden.
 *
 * Any pattern already sounding is stopped first, so overlapping calls (a repeat
 * arriving while a preview runs, or a fast re-pick in the sound picker) never
 * stack into noise.
 *
 * The alarm loops by scheduling the next pattern as this one ends (see
 * `useAlertAlarm`, whose repeat interval is {@link ALARM_PATTERN_MS}), so the
 * patterns tile rather than leaving a gap between short chimes.
 *
 * Browser autoplay failures intentionally stay silent.
 */
export function playAlertSound(soundId?: string): void {
  const context = getAudioContext();
  if (!context) return;

  stopAlertSound();

  const sound = getAlarmSound(soundId ?? useAlertNotifyStore.getState().alarmSoundId);
  const run = () => {
    const bus = context.createGain();
    bus.gain.setValueAtTime(1, context.currentTime);
    bus.connect(context.destination);
    currentBus = bus;
    sound.schedule(context, context.currentTime, bus);
    // Release the handle once the pattern has run its course, so
    // `isAlertSoundPlaying()` reports honestly without needing a timer elsewhere.
    setTimeout(() => {
      if (currentBus === bus) {
        bus.disconnect();
        currentBus = null;
      }
    }, ALARM_PATTERN_MS);
  };

  if (context.state === "suspended") {
    void context.resume().then(run).catch(() => {});
    return;
  }
  run();
}

/**
 * Audition a pattern from the settings picker, or stop the one playing.
 *
 * Returns true when a preview STARTED, false when this call stopped one — so the
 * caller can label its control accordingly.
 */
export function toggleAlarmPreview(soundId: string): boolean {
  if (isAlertSoundPlaying()) {
    stopAlertSound();
    return false;
  }
  playAlertSound(soundId);
  return true;
}

export { ALARM_PATTERN_MS };
